<template>
  <ChooseItemsModal
    v-if="showProductSelectionModal"
    :display-dialog="showProductSelectionModal"
    header="storeParameters.services.productRecommendations.modalProductsTitle"
    name="ShopsProducts"
    :fields="{ id: 'id_shop_product', label: 'name', elementId: 'id_product' }"
    :items="selectedProductIds"
    :default-filters="productFilters"
    :enable-variants="true"
    @on-close-dialog="showProductSelectionModal = false"
    @on-choose-items="onChooseProductsAndVariants"
  />
  <CategorySelection
    v-if="showProductCategoryListSelectionModal"
    :default-lang="defaultLang"
    :available-categories-arr="availableCategories"
    :chosen-categories-arr="shopsProductsCategories"
    :category-search-str="categorySearchStr"
    @close-action-modal="closeProductCategoryListSelectionModal"
  />
  <SupplierSelection
    v-if="showManufacturerSelectionModal"
    :available-manufacturers-arr="availableManufacturers"
    :chosen-manufacturers-arr="manufacturers"
    :manufacturer-search-str="manufacturerSearchStr"
    @close-action-modal="closeManufacturerSelectionModal"
  />

  <div class="mb-4 alert-info">
    {{ t('storeParameters.services.productRecommendations.title') }}
  </div>

  <div class="selection-cards">
    <!-- Products -->
    <div
      class="selection-card"
      @click="showProductSelectionModal = true"
    >
      <div class="card-icon">
        <i class="fa-thin fa-box" />
      </div>
      <div class="card-content">
        <span class="card-label">{{ t('storeParameters.services.productRecommendations.excludeProductsLabel') }}</span>
        <span class="card-action">{{ t('storeParameters.services.productRecommendations.excludeProductsBtn') }}</span>
      </div>
      <div
        class="card-badge"
        :class="{ 'has-items': selectedProductsCount > 0 }"
      >
        <span class="badge-count">{{ selectedProductsCount }}</span>
      </div>
      <i class="fa-solid fa-chevron-right card-arrow" />
    </div>

    <!-- Categories -->
    <div
      class="selection-card"
      @click="showProductCategoryListSelectionModal = true"
    >
      <div class="card-icon">
        <i class="fa-thin fa-folder-tree" />
      </div>
      <div class="card-content">
        <span class="card-label">{{ t('storeParameters.services.productRecommendations.excludeCategoriesLabel') }}</span>
        <span class="card-action">{{ t('storeParameters.services.productRecommendations.excludeCategoriesBtn') }}</span>
      </div>
      <div
        class="card-badge"
        :class="{ 'has-items': shopsProductsCategories.length > 0 }"
      >
        <span class="badge-count">{{ shopsProductsCategories.length }}</span>
      </div>
      <i class="fa-solid fa-chevron-right card-arrow" />
    </div>

    <!-- Manufacturers -->
    <div
      class="selection-card"
      @click="showManufacturerSelectionModal = true"
    >
      <div class="card-icon">
        <i class="fa-thin fa-industry" />
      </div>
      <div class="card-content">
        <span class="card-label">{{ t('storeParameters.services.productRecommendations.excludeSupplierLabel') }}</span>
        <span class="card-action">{{ t('storeParameters.services.productRecommendations.excludeSupplierBtn') }}</span>
      </div>
      <div
        class="card-badge"
        :class="{ 'has-items': manufacturers.length > 0 }"
      >
        <span class="badge-count">{{ manufacturers.length }}</span>
      </div>
      <i class="fa-solid fa-chevron-right card-arrow" />
    </div>
  </div>

  <div class="mt-4">
    <SpmButton
      :label="t('save')"
      :loading="saveLoading"
      class="p-button p-button-primary w-full"
      @click="saveRecommendationExclusion"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ChooseItemsModal from '@/components/modals/ChooseItemsModal.vue';
import CategorySelection from '@/views/shop/tabs/modals/CategorySelection.vue';
import SupplierSelection from '@/views/shop/tabs/modals/SupplierSelection.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  Maybe, ShopsManufacturers, ShopsProducts, ShopsProductsCategoriesList,
} from '@/types/generated-types/graphql';
import { StringMap } from '@/types';
import { OperatorType } from '@/types/generated-types/graphql';
import {
  fetchShopConfigurationsNest,
  saveShopConfigurationsNest,
  saveShopParamsOnRedis,
} from '@/composables/shop/ShopsConfiguration';
import { showToastSuccess, showToastError } from '@/helpers';
import { unserialize, serialize } from 'php-serialize';
import { loadShopsLang, UserState } from '@/composables/User';

interface SelectedItem {
  combinations?: Array<number | string | Record<string, any>>;
  id: string;
  name: string;
  spmId: number;
  image?: string;
}

interface SelectedRecord {
  operator: string;
  selected: SelectedItem[];
}

interface Selection {
  selection: string;
}

export default defineComponent({
  name: 'ProductRecommendationServiceParameter',

  components: {
    SpmButton,
    ChooseItemsModal,
    CategorySelection,
    SupplierSelection,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },
  },

  emits: ['close'],

  setup(props) {
    const { t } = useI18n();
    const defaultLang = ref('');
    const saveLoading = ref(false);

    const showProductSelectionModal = ref(false);
    const showProductCategoryListSelectionModal = ref(false);
    const showManufacturerSelectionModal = ref(false);
    const shopsProductsCategories = ref<ShopsProductsCategoriesList[]>([]);
    const availableCategories = ref<ShopsProductsCategoriesList[]>([]);
    const categorySearchStr = ref('');
    const manufacturers = ref<ShopsManufacturers[]>([]);
    const availableManufacturers = ref<ShopsManufacturers[]>([]);
    const manufacturerSearchStr = ref('');
    const products = ref<ShopsProducts[]>([]);
    const productVariants = ref<StringMap[]>([]);
    const selectedProductsCount = ref(0);
    const selectedProductIds = ref('');

    const updateSelectedIds = () => {
      const tokensByProduct = new Map<string, string[]>();

      products.value.forEach((product) => {
        const id = String(product.id_shop_product);
        if (!tokensByProduct.has(id)) {
          tokensByProduct.set(id, []);
        }
      });

      productVariants.value.forEach((variant) => {
        const parentId = String(variant.id_shop_product);
        if (!tokensByProduct.has(parentId)) {
          tokensByProduct.set(parentId, []);
        }
        const combId = String(variant._variantElementId ?? '');
        if (combId) {
          tokensByProduct.get(parentId)!.push(combId);
        }
      });

      const tokens: string[] = [];
      tokensByProduct.forEach((combinationIds, productId) => {
        if (combinationIds.length > 0) {
          tokens.push(`${productId}[${combinationIds.join(';')}]`);
        } else {
          tokens.push(productId);
        }
      });

      selectedProductIds.value = tokens.join(',');
      selectedProductsCount.value = products.value.length + productVariants.value.length;
    };
    const productFilters = computed(() => ([
      { field: 'lang', value: defaultLang.value, operator: OperatorType.Equals },
      { field: 'active', value: true, operator: OperatorType.Equals },
    ]));

    const onChooseProductsAndVariants = (selectedItems: StringMap[]) => {
      showProductSelectionModal.value = false;
      products.value = selectedItems
        .filter((item) => item._isVariant !== 'true')
        .map((item) => ({
          id_shop_product: Number(item.id_shop_product),
          id_shop: props.shopId,
          id_product: Number(item.id_product),
          lang: '',
          name: String(item.name ?? ''),
          link: '',
          image_link: null,
          currency: '',
          price: 0,
          quantity: 0,
          active: true,
          date_creation: '',
          date_modification: '',
          date: '',
        }));
      productVariants.value = selectedItems
        .filter((item) => item._isVariant === 'true')
        .map((item) => ({
          id_shop_product: String(item.id_shop_product ?? ''),
          id_product: String(item.id_product ?? ''),
          name: String(item.name ?? ''),
          _isVariant: 'true',
          _variantId: String(item._variantId ?? ''),
          _variantElementId: String(item._variantElementId ?? ''),
          _combinationName: String(item._combinationName ?? ''),
        }));
      updateSelectedIds();
    };

    const closeProductCategoryListSelectionModal = (list: ShopsProductsCategoriesList[], categoriesAvailable: ShopsProductsCategoriesList[], searchStr: string) => {
      showProductCategoryListSelectionModal.value = false;
      shopsProductsCategories.value = list;
      availableCategories.value = categoriesAvailable;
      categorySearchStr.value = searchStr;
    };

    const closeManufacturerSelectionModal = (list: ShopsManufacturers[], manufacturersAvailable: ShopsManufacturers[], searchStr: string) => {
      showManufacturerSelectionModal.value = false;
      manufacturers.value = list;
      availableManufacturers.value = manufacturersAvailable;
      manufacturerSearchStr.value = searchStr;
    };

    const saveRecommendationExclusion = async () => {
      const configurationsToSave: { key: string; value: string; lang?: string }[] = [];

      if (
        (products.value && products.value.length > 0)
        || (productVariants.value && productVariants.value.length > 0)
      ) {
        const selectedProductsMap = new Map<number, {
          id: number;
          name: string;
          image: Maybe<string> | undefined;
          spmId: number;
          combinations: Array<{ id: number; spmId: number; name: string }>;
        }>();

        products.value.forEach((product) => {
          selectedProductsMap.set(product.id_shop_product, {
            id: product.id_product,
            name: product.name,
            image: product.image_link,
            spmId: product.id_shop_product,
            combinations: [],
          });
        });

        productVariants.value.forEach((variant) => {
          const parentSpmId = Number(variant.id_shop_product ?? 0);
          if (!selectedProductsMap.has(parentSpmId)) {
            selectedProductsMap.set(parentSpmId, {
              id: Number(variant.id_product ?? 0),
              name: String(variant.name ?? ''),
              image: undefined,
              spmId: parentSpmId,
              combinations: [],
            });
          }
          const productEntry = selectedProductsMap.get(parentSpmId);
          if (productEntry && variant._variantId) {
            productEntry.combinations.push({
              id: Number(variant._variantElementId ?? 0),
              spmId: Number(variant._variantId),
              name: String(variant._combinationName ?? ''),
            });
          }
        });

        const selectedProducts = Array.from(selectedProductsMap.values()).map((productEntry) => ({
          id: productEntry.id,
          name: productEntry.name,
          image: productEntry.image,
          spmId: productEntry.spmId,
          combinations: productEntry.combinations,
        }));

        const productSelection = {
          selection: JSON.stringify({
            operator: 'or',
            selected: selectedProducts,
          }),
        };

        configurationsToSave.push({
          key: 'shopRequiredDataCrosselling_product_picker_crosselling_exclude',
          value: serialize(productSelection),
        });
      } else {
        configurationsToSave.push({
          key: 'shopRequiredDataCrosselling_product_picker_crosselling_exclude',
          value: serialize({}),
        });
      }

      if (shopsProductsCategories.value && shopsProductsCategories.value.length > 0) {
        const selectedProductsCategories: { id: number; spmId: number; name: string; combinations: {} }[] = [];
        shopsProductsCategories.value.forEach((category) => {
          selectedProductsCategories.push({
            id: category.id_category,
            name: category.name,
            spmId: category.id_shop_product_category_list,
            combinations: {},
          });
        });

        const categorySelection = {
          selection: JSON.stringify({
            operator: 'or',
            selected: selectedProductsCategories,
          }),
        };

        configurationsToSave.push({
          key: 'shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude',
          value: serialize(categorySelection),
        });
      } else {
        configurationsToSave.push({
          key: 'shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude',
          value: serialize({}),
        });
      }

      if (manufacturers.value && manufacturers.value.length > 0) {
        const selectedManufacturers: { id: string; spmId: number; name: string; combinations: {} }[] = [];
        manufacturers.value.forEach((manufacturer) => {
          selectedManufacturers.push({
            id: manufacturer.id_manufacturer,
            name: manufacturer.name,
            spmId: manufacturer.id_shop_manufacturer,
            combinations: {},
          });
        });

        const manufacturerSelection = {
          selection: JSON.stringify({
            operator: 'or',
            selected: selectedManufacturers,
          }),
        };

        configurationsToSave.push({
          key: 'shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude',
          value: serialize(manufacturerSelection),
        });
      } else {
        configurationsToSave.push({
          key: 'shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude',
          value: serialize({}),
        });
      }

      saveLoading.value = true;

      try {
        const result = await saveShopConfigurationsNest(props.shopId, configurationsToSave);
        if (result) {
          await saveShopParamsOnRedis(props.shopId);
          showToastSuccess('savedSuccessful');
        } else {
          showToastError('GENERIC_ERROR');
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        saveLoading.value = false;
      }
    };

    onMounted(async () => {
      await loadShopsLang(props.shopId);
      const shopsLangs = UserState.activeShop?.langs ?? [];
      defaultLang.value = shopsLangs.find((lang) => lang.default)?.id ?? shopsLangs[0]?.id ?? '';

      let unserializedRecord: Selection = {
        selection: JSON.stringify({
          operator: 'or',
          selected: [
            {
              id: '',
              name: '',
              spmId: 0,
              image: '',
            },
          ],
        }),
      };
      const keys = [
        'shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude',
        'shopRequiredDataCrosselling_product_picker_crosselling_exclude',
        'shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude',
      ];
      const resultData = await fetchShopConfigurationsNest(props.shopId, keys);

      if (resultData) {
        Object.keys(resultData).forEach((key) => {
          const value = resultData[key];
          if (value) {
            unserializedRecord = unserialize(value);
            if (unserializedRecord.selection && unserializedRecord.selection !== '') {
              const jsonRecord: SelectedRecord = JSON.parse(unserializedRecord.selection);
              if (key === 'shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude') {
                (jsonRecord.selected ?? []).forEach((selectedRecord: SelectedItem) => {
                  const productCategories = {
                    id_shop_product_category_list: selectedRecord.spmId,
                    id_shop: props.shopId,
                    id_category: Number(selectedRecord.id),
                    lang: '',
                    name: selectedRecord.name,
                    link: '',
                    active: '1',
                    date_creation: '',
                    date_modification: '',
                    date: '',
                  };
                  shopsProductsCategories.value.push(productCategories);
                });
              } else if (key === 'shopRequiredDataCrosselling_product_picker_crosselling_exclude') {
                (jsonRecord.selected ?? []).forEach((selectedRecord: SelectedItem) => {
                  const combinations = Array.isArray(selectedRecord.combinations)
                    ? selectedRecord.combinations
                    : [];
                  if (combinations.length > 0) {
                    combinations.forEach((combination: any) => {
                      const spmVariantId = Number(
                        combination?.spmId
                        ?? combination?.id_shop_product_attribute
                        ?? combination
                        ?? 0,
                      );
                      if (!spmVariantId) {
                        return;
                      }
                      productVariants.value.push({
                        id_shop_product: String(selectedRecord.spmId),
                        id_product: String(selectedRecord.id),
                        name: selectedRecord.name,
                        _isVariant: 'true',
                        _variantId: String(spmVariantId),
                        _variantElementId: String(combination?.id ?? combination?.id_combination ?? ''),
                        _combinationName: String(combination?.name ?? ''),
                      });
                    });
                    return;
                  }
                  const productRecord = {
                    id_shop_product: selectedRecord.spmId,
                    id_shop: props.shopId,
                    id_product: Number(selectedRecord.id),
                    lang: '',
                    name: selectedRecord.name,
                    link: '',
                    image_link: selectedRecord.image ?? '',
                    currency: '',
                    price: 0,
                    quantity: 0,
                    active: true,
                    date_creation: '',
                    date_modification: '',
                    date: '',
                  };
                  products.value.push(productRecord);
                });
              } else if (key === 'shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude') {
                (jsonRecord.selected ?? []).forEach((selectedRecord: SelectedItem) => {
                  const manufacturer = {
                    id_shop_manufacturer: selectedRecord.spmId,
                    id_shop: props.shopId,
                    id_manufacturer: selectedRecord.id,
                    name: selectedRecord.name,
                    active: '1',
                    date_creation: '',
                    date_modification: '',
                    date: '',
                  };
                  manufacturers.value.push(manufacturer);
                });
              }
            }
          }
        });
        updateSelectedIds();
      }
    });

    return {
      t,
      defaultLang,
      saveLoading,
      showProductSelectionModal,
      onChooseProductsAndVariants,
      showProductCategoryListSelectionModal,
      closeProductCategoryListSelectionModal,
      showManufacturerSelectionModal,
      closeManufacturerSelectionModal,
      products,
      shopsProductsCategories,
      manufacturers,
      saveRecommendationExclusion,
      selectedProductIds,
      selectedProductsCount,
      productFilters,
      availableCategories,
      categorySearchStr,
      availableManufacturers,
      manufacturerSearchStr,
    };
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/utilities/_variables.scss';

.alert-info {
  color: #666;
  background-color: #f6f6f6;
  padding: 1rem 1.25rem;
  border-radius: .5rem;
  border: none;
  font-size: 0.9rem;
  line-height: 1.5;
}

.selection-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.selection-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: $white;
  border: 1px solid $solitude;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $brand-color-primary;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .card-arrow {
      color: $brand-color-primary;
      transform: translateX(3px);
    }
  }

  .card-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $extra-light-grey;
    border-radius: 8px;
    flex-shrink: 0;

    i {
      font-size: 1.5rem;
      color: $brand-color-primary;
    }
  }

  .card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .card-label {
      font-size: 0.75rem;
      color: $mid-grey;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .card-action {
      font-size: 0.95rem;
      color: $tuna;
      font-weight: 500;
    }
  }

  .card-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 0.5rem;
    background: $solitude;
    border-radius: 16px;
    flex-shrink: 0;

    .badge-count {
      font-size: 0.875rem;
      font-weight: 600;
      color: $mid-grey;
    }

    &.has-items {
      background: $brand-color-primary;

      .badge-count {
        color: $white;
      }
    }
  }

  .card-arrow {
    color: $mid-grey;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
}
</style>
